import {
  Component,
  h,
  Element,
  Listen,
  State,
  Watch,
  Host,
  Prop,
} from "@stencil/core"
import {
  menuFlyoutReducer,
  getInitialState,
  MenuFlyoutActions,
  MenuFlyoutState,
} from "../menu-flyout-store"
import {
  getNextFromList,
  getPreviousFromList,
  StoreConnection,
  StateHandle,
  getAppearance,
} from "../../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-menu-flyout-list",
  styleUrl: "menu-flyout-list.scss",
  shadow: true,
})
export class MenuFlyoutList {
  private store: StoreConnection<this, MenuFlyoutState, MenuFlyoutActions>
  private trapFocusEl?: HTMLSdxTrapFocusElement

  @Element() public el!: HTMLSdxMenuFlyoutListElement

  @State() public state!: StateHandle<MenuFlyoutState>

  /**
   * Description of the list read by the screen reader.
   */
  @Prop() public srHint: string = ""

  @Watch("state")
  public stateChanged({}, prev: StateHandle<MenuFlyoutState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.display !== prevState.display) {
      this.displayChanged(state)
    }
  }

  @Listen("keydown")
  public onKeyDown(e: KeyboardEvent) {
    // do not save childs at componentDidLoad because they could be added dynamically or parsed async
    const getChildsArray = () =>
      Array.from(this.el.querySelectorAll("sdx-menu-flyout-list-item"))

    switch (e.key) {
      case "ArrowDown":
        const nextEl = getNextFromList(
          getChildsArray(),
          document.activeElement,
        ) as HTMLSdxMenuFlyoutListItemElement
        nextEl.doFocus()
        e.preventDefault()
        break

      case "ArrowUp":
        const previousEl = getPreviousFromList(
          getChildsArray(),
          document.activeElement,
        ) as HTMLSdxMenuFlyoutListItemElement
        previousEl.doFocus()
        e.preventDefault()
        break

      case " ":
        const currentEl =
          document.activeElement as HTMLSdxMenuFlyoutListItemElement
        currentEl.doClick()
        e.preventDefault()
        break
    }
  }

  constructor() {
    this.store = new StoreConnection(
      this,
      menuFlyoutReducer,
      getInitialState(),
      ["display"],
    )
  }

  public connectedCallback() {
    // Register self
    this.store.set("contentEl", this.el)
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private displayChanged(state: MenuFlyoutState) {
    if (state.display === "open") {
      this.trapFocusEl?.doFocus()
    }
  }

  private getHostClassNames() {
    return {
      [getAppearance(this.el)]: true,
    }
  }

  public render() {
    return (
      <Host
        role="dialog"
        class={this.getHostClassNames()}
        aria-label={this.srHint}
      >
        <sdx-trap-focus
          ref={(el) => (this.trapFocusEl = el)}
          lang={this.el.lang}
        >
          <div role="list">
            <slot />
          </div>
        </sdx-trap-focus>
      </Host>
    )
  }
}
